import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import type { EventAnalyticsEventItem } from '@services/event-analytics/types'

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card'

interface TopApartmentRow {
  id: number
  count: number
  location: string | null
}

function aggregateTopApartments(
  items: EventAnalyticsEventItem[],
  limit: number
): TopApartmentRow[] {
  const counts = new Map<number, { count: number; location: string | null }>()

  for (const row of items) {
    const rawId = row.properties.apartment_id
    const id =
      typeof rawId === 'number'
        ? rawId
        : typeof rawId === 'string'
          ? Number.parseInt(rawId, 10)
          : NaN
    if (Number.isNaN(id)) continue

    const existing = counts.get(id) ?? { count: 0, location: null }
    const location =
      existing.location ??
      (row.properties.location ? String(row.properties.location) : null)
    counts.set(id, { count: existing.count + 1, location })
  }

  return [...counts.entries()]
    .sort((a, b) => b[1].count - a[1].count)
    .slice(0, limit)
    .map(([id, meta]) => ({ id, ...meta }))
}

interface EventAnalyticsTopApartmentsProps {
  items?: EventAnalyticsEventItem[]
  loading?: boolean
}

export function EventAnalyticsTopApartments({
  items = [],
  loading = false
}: EventAnalyticsTopApartmentsProps) {
  const { t } = useTranslation('eventAnalytics')

  const top = useMemo(() => aggregateTopApartments(items, 5), [items])

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('topApartments.title')}</CardTitle>
        <CardDescription>{t('topApartments.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-40 animate-pulse rounded-xl bg-ink/[0.04]" />
        ) : top.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {t('topApartments.empty')}
          </p>
        ) : (
          <ol className="space-y-3">
            {top.map((apt, index) => (
              <li
                key={apt.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-ink/[0.06] px-3 py-2.5"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                    {index + 1}
                  </span>
                  <div className="min-w-0">
                    <Link
                      to={`/admin/apartments/${apt.id}`}
                      className="font-medium text-primary hover:underline"
                    >
                      {t('topApartments.apartmentLink', { id: apt.id })}
                    </Link>
                    {apt.location ? (
                      <p className="truncate text-xs text-muted-foreground">
                        {apt.location}
                      </p>
                    ) : null}
                  </div>
                </div>
                <span className="shrink-0 text-sm font-semibold text-ink">
                  {t('topApartments.clicks', { count: apt.count })}
                </span>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  )
}
